import { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";
import FilterBtn from "../../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../../Assets/Icons/btn-filters-up.png";
import CustomLoadingOverlay from "../../../../../Components/CustomLoadingOverlay";
import { useBayReport } from "./useBayReport";
import { getColumns } from "./columns";
import Filter from "./Filter";

const INITIAL_FILTER_STATE = {
  bay_code: [],
  start_date: null,
  end_date: null,
  category_code: [],
  group_by: "",
};

const BayReport = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const {
    reportData,
    bays,
    categories,
    groups,
    loading,
    error,
    fetchReportData,
  } = useBayReport({
    autoFetch: true,
  });

  const columns = getColumns(submittedData.group_by || "");

  const handleChangeFilter = (field) => (event, value) => {
    const newValue = value || (event && event.target.value) || "";

    setFilterData((prev) => {
      const updatedData = { ...prev };

      if (
        field.includes("Date") ||
        field.includes("start") ||
        field.includes("end")
      ) {
        updatedData[field] = newValue || null;
      } else {
        updatedData[field] = newValue;
      }

      // Clear category_code filter if group_by is not "category"
      if (field === "group_by" && newValue !== "category") {
        updatedData.category_code = [];
      }

      // Clear category_code filter if group_by changes to something other than "category"
      if (
        field === "group_by" &&
        prev.group_by === "category" &&
        newValue !== "category"
      ) {
        updatedData.category_code = [];
      }

      return updatedData;
    });
  };

  const handleFilter = (filterDataToSubmit) => {
    // Update submittedData with the original filter data for active filters display
    setSubmittedData(filterDataToSubmit);

    // Pre-process filter data to omit empty fields
    const processedFilterData = {};

    // Only add bay_code if it has values
    if (filterDataToSubmit.bay_code && filterDataToSubmit.bay_code.length > 0) {
      processedFilterData.bay_code = filterDataToSubmit.bay_code.join(",");
    }

    // Only add category_code if it has values
    if (
      filterDataToSubmit.category_code &&
      filterDataToSubmit.category_code.length > 0
    ) {
      processedFilterData.category_code =
        filterDataToSubmit.category_code.join(",");
    }

    // Add date fields if they have values
    if (filterDataToSubmit.start_date) {
      processedFilterData.start_date = filterDataToSubmit.start_date;
    }

    if (filterDataToSubmit.end_date) {
      processedFilterData.end_date = filterDataToSubmit.end_date;
    }

    // Add group_by if it has value
    if (filterDataToSubmit.group_by) {
      processedFilterData.group_by = filterDataToSubmit.group_by;
    }

    // Pagination params
    processedFilterData.page = 1;
    processedFilterData.pagination = 50;

    fetchReportData(processedFilterData);
  };

  const onFilterClick = () => {
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    if (field === "bay_code") {
      newFilterData.bay_code = [];
    } else if (field === "category_code") {
      newFilterData.category_code = [];
    } else if (field === "group_by") {
      newFilterData.group_by = "";
      newFilterData.category_code = []; // Also clear category_code
      delete newSubmittedData.category_code; // Also clear from submitted data
    } else {
      newFilterData[field] =
        field.includes("start") || field.includes("end") ? null : "";
    }

    delete newSubmittedData[field];
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    handleFilter(INITIAL_FILTER_STATE);
  };

  if (error) {
    return (
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <div>Error: {error}</div>
          <button onClick={fetchReportData}>Retry</button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Bay Report
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            />
          </Box>
        </Box>

        <Filter
          filterOpen={filterOpen}
          filterData={filterData}
          handleChangeFilter={handleChangeFilter}
          handleFilter={onFilterClick}
          handleClearFilters={handleClearFilters}
          handleRemove={handleRemove}
          submittedData={submittedData}
          bays={bays}
          categories={categories}
          groups={groups}
        />

        <DataGrid
          rows={reportData}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          loading={loading}
          slots={{
            loadingOverlay: CustomLoadingOverlay,
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              fontWeight: 500,
              color: "#48505E",
            },
          }}
          getRowId={(row) => row.bay_code}
        />
      </Box>
    </Box>
  );
};

export default BayReport;
